import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useMutation } from '@grandvisionhq/graphql';
import { useCartStorage } from '@grandvisionhq/lib-utils-cart';
import { useIntl, useApiConfig } from '@grandvisionhq/state';
import { useDispatchTrackErrorEvent } from '@grandvisionhq/svc-tealium/events';
import '../context/Cart/context.js';
import '@grandvisionhq/common';
import '@grandvisionhq/utils';
import { useCartSettings } from '../context/CartSettings/context.js';
import '../models/add-to-cart/add-to-cart.js';
import '../models/cart/cart.js';
import '../models/subscription/subscription-details.js';
import '../models/checkoutState/checkoutState.js';
import '../utils/cart-count-var.js';
import '../utils/cart-id-var.js';
import '@grandvisionhq/commercetools';
import { getValidationErrorCodes } from '../utils/get-validation-error-codes.js';
import '../utils/get-unrecoverable-validation-errors.js';
import { lineItemDataMapper } from '../utils/line-item-data-mapper.js';
import '../utils/get-failure-error-codes.js';
import '../utils/get-validation-errors-by-line-item.js';
import { toVisualLineItem } from '../mappers/view/visual-line-item/cart-line-item.js';
import '../operations/fragments/cart-details.js';
import '../operations/fragments/cart-validation-errors.js';
import '../operations/fragments/recover-inactive-cart-failure.js';
import '../operations/fragments/recover-inactive-cart-result.js';
import '../operations/mutations/add-cart-line-items.js';
import '../operations/mutations/add-cart-voucher-codes.js';
import '../operations/mutations/create-cart.js';
import '../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../operations/mutations/create-cart-from-order.js';
import '../operations/mutations/duplicate-cart-line-items.js';
import '../operations/mutations/remove-cart-voucher-codes.js';
import '../operations/mutations/remove-cart-line-items.js';
import '../operations/mutations/update-cart-line-items.js';
import { REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM } from '../operations/mutations/replace-line-item-contact-lenses-bundle-benelux.js';
import '../operations/mutations/recover-inactive-cart.js';
import '../operations/queries/get-cart.js';
import '../operations/queries/get-total-line-items.js';
import '../operations/queries/get-cart-estimated-delivery.js';
import { mapLineItemToDataLayerEvent } from '../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import '@grandvisionhq/payments';
import '../mappers/analytics/tealium/line-item-to-tealium-product.js';
import { errorsMapping } from '../mappers/error-codes.js';
import { dispatchMiniCartEvent } from '../modules/MiniCart/utils/event.js';

const useReplaceLineItemContactLensesBundleBenelux = ()=>{
    const intl = useIntl();
    const settings = useCartSettings();
    const [cart] = useCartStorage();
    const { apiUrl  } = useApiConfig();
    const [replaceCartLineItem, replaceCartLineItemMutation] = useMutation(REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM);
    const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
    const handleTrackErrorTaelium = React.useCallback((errorDetails, errorMessage)=>dispatchTrackErrorEvent(()=>Promise.resolve(swcHelpers.objectSpread({
                Error_Source: 'Server',
                Error_Code: 'Replace line item contact lenses bundle error',
                Error_Details: errorDetails
            }, errorMessage ? {
                Error_Message: errorMessage
            } : {}))
        )
    , [
        dispatchTrackErrorEvent
    ]);
    const handleOnCompleted = React.useCallback((replacedLineItem, data, onSuccess, onValidationError, onError)=>{
        const result = data.replaceContactLensBundleBeneluxCartLineItem;
        if (!result.success && result.validationErrors) {
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            return;
        }
        if (!result.cart) {
            if (onError) {
                handleTrackErrorTaelium(errorsMapping.CART_IS_EMPTY);
                onError(errorsMapping.CART_IS_EMPTY);
            }
            return;
        }
        const lineItemsInCart = result.cart.lineItems;
        const addedLineItems = lineItemsInCart.filter(({ sku  })=>sku === replacedLineItem.newSku
        );
        const addedLineItemsMappedToViewModel = lineItemDataMapper(addedLineItems, settings, intl);
        const addedVisualLineItems = addedLineItems.map((lineItem)=>toVisualLineItem(lineItem, intl, settings, apiUrl)
        ).filter((lineItem)=>Boolean(lineItem)
        );
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMappedToViewModel, result.cart.id);
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        dispatchMiniCartEvent('updated', addedVisualLineItems);
        onSuccess && onSuccess(addedLineItemsMappedToViewModel);
    }, [
        apiUrl,
        handleTrackErrorTaelium,
        intl,
        settings
    ]);
    const replaceLineItem = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError) {
            if (!cart.cartId) {
                if (onError) {
                    onError(errorsMapping.MISSING_CART_ID);
                    handleTrackErrorTaelium(errorsMapping.MISSING_CART_ID);
                }
                return;
            }
            try {
                const { data , errors  } = yield replaceCartLineItem({
                    variables: {
                        cartInput: {
                            cartId: cart.cartId,
                            lineItemToReplace: lineItemDrafts
                        }
                    }
                });
                if (errors) {
                    if (onError) {
                        onError(errorsMapping.SOMETHING_WENT_WRONG);
                        handleTrackErrorTaelium(errorsMapping.SOMETHING_WENT_WRONG);
                    }
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onSuccess, onValidationError, onError);
                }
            } catch (error) {
                console.error('Replace line item operation failed: ', error);
                handleTrackErrorTaelium('Replace line item operation failed', errorsMapping.SOMETHING_WENT_WRONG);
                if (onError) {
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError, onError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart.cartId,
        handleOnCompleted,
        handleTrackErrorTaelium,
        replaceCartLineItem
    ]);
    const { called , loading , error: error1 , data: data1  } = replaceCartLineItemMutation;
    var _cart;
    const newCart = (_cart = data1 === null || data1 === void 0 ? void 0 : data1.replaceContactLensBundleBeneluxCartLineItem.cart) !== null && _cart !== void 0 ? _cart : null;
    return [
        replaceLineItem,
        {
            called,
            loading,
            error: error1,
            cart: newCart
        }
    ];
};

export { useReplaceLineItemContactLensesBundleBenelux };
//# sourceMappingURL=use-replace-line-item-contact-lenses-bundle-benelux.js.map
